import React from 'react';
import { Alert, Button, Card, Dialog } from '@betareader/design-system';
import styled from 'styled-components';
import { authUtil } from 'src/utils';
import Icon from '../AppIcon';

type Direction = {
  start: number;
  end: number;
  preset: string;
  custom?: string;
};
type Take = {
  id: string;
  durationMs: number;
  creditsCharged: number;
  createdAt: string;
  textCurrent: boolean;
  renderCurrent?: boolean;
};
type Paragraph = {
  index: number;
  text: string;
  wordCount: number;
  credits: number;
  directions: Direction[];
  directionHash: string;
  status: string;
  audioCurrent: boolean;
  needsRegeneration: boolean;
  sourceChanged: boolean;
  selectedTakeId?: string;
  takes: Take[];
  error?: string;
};
type Studio = {
  title: string;
  voice: { displayName?: string; model?: string };
  chapterStatus: string;
  chapterModel?: string | null;
  paragraphs: Paragraph[];
  balance: number;
  canPackage: boolean;
  missingParagraphs: number;
  packagingStatus: string;
  packagingError?: string;
  supportsEmotionTags: boolean;
};
type Pending = { paragraphs: Paragraph[] };
type MarkerView = 'color-cue' | 'tags';
type SelectedDirection = { paragraphIndex: number; start: number; end: number };

const presets = [
  { id: 'happy', emoji: '😊', label: 'Happy' },
  { id: 'sad', emoji: '😢', label: 'Sad' },
  { id: 'angry', emoji: '😠', label: 'Angry' },
  { id: 'curious', emoji: '🧐', label: 'Curious' },
  { id: 'surprised', emoji: '😮', label: 'Surprised' },
  { id: 'whispering', emoji: '🤫', label: 'Whispering' },
];
const creditLabel = (credits: number) =>
  `${credits} ${credits === 1 ? 'credit' : 'credits'}`;
const inProgress = (paragraph: Paragraph) =>
  ['queued', 'generating'].includes(paragraph.status);
const tagLabel = (direction: Direction) =>
  direction.custom ||
  (direction.preset === 'whispering' ? 'whispers' : direction.preset);

const Manuscript = styled.div`
  margin: 18px 0;
  font-size: 17px;
  line-height: 1.6;

  @media (max-width: 640px) {
    margin: 14px 0;
    font-size: 16px;
    line-height: 1.55;
  }

  mark {
    --mark-background: #f1ebfa;
    --mark-accent: #7c5da8;
  }
  mark[data-tone='happy'] {
    --mark-background: #fff0c8;
    --mark-accent: #a46b13;
  }
  mark[data-tone='sad'] {
    --mark-background: #e4f0fb;
    --mark-accent: #4572a5;
  }
  mark[data-tone='angry'] {
    --mark-background: #fbe7e5;
    --mark-accent: #ba5d56;
  }
  mark[data-tone='curious'] {
    --mark-background: #e4f3ed;
    --mark-accent: #427e67;
  }
  mark[data-tone='surprised'] {
    --mark-background: #f9e9f4;
    --mark-accent: #a65f91;
  }
  mark[data-tone='whispering'] {
    --mark-background: #eceef2;
    --mark-accent: #687382;
  }

  &[data-marker-view='color-cue'] mark {
    position: relative;
    background: var(--mark-background);
    color: #1b1c1d;
    box-shadow: inset 0 -2px var(--mark-accent);
  }
  &[data-marker-view='color-cue'] mark::before {
    content: attr(data-emotion-emoji);
    position: absolute;
    z-index: 1;
    top: -0.46em;
    left: 0;
    padding: 0 2px;
    border-radius: 3px;
    background: var(--mark-background);
    font-size: 0.62em;
    line-height: 1;
    pointer-events: none;
  }
  &[data-marker-view='tags'] mark {
    background: var(--br-raised-surface);
  }
  &[data-marker-view='tags'] mark::before,
  &[data-marker-view='tags'] mark::after {
    color: #765c94;
    font:
      0.72em ui-monospace,
      SFMono-Regular,
      Menlo,
      monospace;
    white-space: pre;
    user-select: none;
  }
  &[data-marker-view='tags'] mark::before {
    content: attr(data-opening);
  }
  &[data-marker-view='tags'] mark::after {
    content: attr(data-closing);
  }

  mark[role='button'] {
    cursor: pointer;
  }
  mark[role='button']:focus-visible,
  mark[data-selected='true'] {
    outline: 1px solid var(--mark-accent);
    outline-offset: 2px;
  }
`;
const Passage = styled.div`
  margin: 0 0 0.48em;
  padding: 0 0 0 12px;
  white-space: pre-wrap;
  cursor: text;
  user-select: text;
  transition: background 120ms ease;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid #7657af;
    outline-offset: 3px;
  }

  @media (max-width: 640px) {
    margin-bottom: 0.55em;
    padding-left: 10px;
  }

  mark {
    background: transparent;
    border-radius: 2px;
    color: inherit;
  }
`;
const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 7px;
  margin: 8px 0;
`;
const Context = styled.div`
  margin: -0.18em 0 0.55em 14px;
  padding: 0 0 2px 12px;
  color: #555;
  font-size: 13px;

  ${Toolbar} {
    gap: 5px;
    margin: 4px 0;
  }

  @media (max-width: 640px) {
    margin-left: 12px;
    padding-left: 10px;
  }
`;
const DeliveryPicker = styled.div`
  margin: 9px 0 12px;
  padding: 3px 0 3px 10px;
  border-left: 2px solid #c8b8dc;

  > span {
    display: block;
    margin-bottom: 6px;
  }

  ${Toolbar} {
    margin: 5px 0;
  }
`;
const DirectionDetail = styled.div`
  margin: 5px 0 8px;
  border: 1px solid #ece9e5;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 5px 7px;

  .direction-label {
    flex: 0 0 auto;
    color: var(--br-muted);
  }

  .direction-quote {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    color: #777;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;
const TakesPanel = styled.div`
  margin-top: 11px;
  padding-top: 8px;
  border-top: 1px solid #e8e5e0;

  .take-history {
    max-height: 190px;
    margin-top: 6px;
    overflow-y: auto;
    border: 1px solid #ece9e5;
    border-radius: 4px;
  }

  .take-history-row {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 5px 7px;
  }

  .take-history-row + .take-history-row {
    border-top: 1px solid #f1efec;
  }

  .take-history-row > span {
    flex: 1 1 auto;
    min-width: 0;
  }

  audio {
    display: block;
    width: 100%;
    max-width: 620px;
    margin-top: 8px;
  }
`;
const Muted = styled.span`
  color: #777;
  font-size: 13px;
`;

const apiBase = () =>
  `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}`;

const request = async <T,>(
  path: string,
  method = 'GET',
  body?: any
): Promise<T> => {
  const response = await fetch(`${apiBase()}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'br-token': await authUtil.getFreshIdToken(),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.msg || 'Request failed.');
  return payload as T;
};

const renderText = (
  paragraph: Paragraph,
  selectedDirection: SelectedDirection | null,
  onSelectDirection: (direction: Direction) => void
) => {
  const pieces: React.ReactNode[] = [];
  let cursor = 0;
  paragraph.directions.forEach((direction, index) => {
    if (direction.start > cursor)
      pieces.push(paragraph.text.slice(cursor, direction.start));
    const preset = presets.find(item => item.id === direction.preset);
    const label = direction.custom || preset?.label || direction.preset;
    pieces.push(
      <mark
        key={`${index}-${direction.start}`}
        role="button"
        tabIndex={0}
        aria-label={`Select ${label} voice cue for “${paragraph.text.slice(direction.start, direction.end)}”`}
        aria-pressed={
          selectedDirection?.paragraphIndex === paragraph.index &&
          selectedDirection.start === direction.start &&
          selectedDirection.end === direction.end
        }
        title={`Narration direction: ${label}`}
        data-tone={direction.preset}
        data-selected={
          selectedDirection?.paragraphIndex === paragraph.index &&
          selectedDirection.start === direction.start &&
          selectedDirection.end === direction.end
        }
        data-emotion-emoji={preset?.emoji || '✨'}
        data-opening={`[${tagLabel(direction)}] `}
        data-closing={direction.end < paragraph.text.length ? '[neutral] ' : ''}
        onClick={event => {
          if (window.getSelection()?.isCollapsed === false) return;
          event.stopPropagation();
          onSelectDirection(direction);
        }}
        onKeyDown={event => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          event.stopPropagation();
          onSelectDirection(direction);
        }}
      >
        {paragraph.text.slice(direction.start, direction.end)}
      </mark>
    );
    cursor = direction.end;
  });
  pieces.push(paragraph.text.slice(cursor));
  return pieces;
};

type Props = {
  bookId: string;
  contentVersionId: string;
  partId: string;
  onClose: () => void;
};

const AudiobookParagraphStudio: React.FC<Props> = ({
  bookId,
  contentVersionId,
  partId,
  onClose,
}) => {
  const base = `/books/${bookId}/content/${contentVersionId}/audio`;
  const chapterBase = `${base}/chapters/${partId}`;
  const [data, setData] = React.useState<Studio | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null);
  const [selection, setSelection] = React.useState<{
    index: number;
    start: number;
    end: number;
  } | null>(null);
  const [custom, setCustom] = React.useState('');
  const [markerView, setMarkerView] = React.useState<MarkerView>('color-cue');
  const [selectedDirection, setSelectedDirection] =
    React.useState<SelectedDirection | null>(null);
  const [expandedTakesIndex, setExpandedTakesIndex] = React.useState<
    number | null
  >(null);
  const [pending, setPending] = React.useState<Pending | null>(null);
  const [playUrl, setPlayUrl] = React.useState<string | null>(null);
  const [playingTake, setPlayingTake] = React.useState<string | null>(null);
  const textRefs = React.useRef<Record<number, HTMLDivElement | null>>({});

  const refresh = React.useCallback(async () => {
    setData(await request<Studio>(`${chapterBase}/production`));
  }, [chapterBase]);

  React.useEffect(() => {
    let active = true;
    request<Studio>(`${chapterBase}/production`)
      .then(next => {
        if (active) setData(next);
      })
      .catch(err => {
        if (active) setError(err.message);
      });
    return () => {
      active = false;
    };
  }, [chapterBase]);

  React.useEffect(() => {
    if (
      !data?.paragraphs.some(inProgress) &&
      !['assembling', 'packaging'].includes(data?.packagingStatus || '')
    )
      return;
    const timer = window.setInterval(
      () => refresh().catch(() => undefined),
      3000
    );
    return () => window.clearInterval(timer);
  }, [data, refresh]);

  const selectText = (index: number) => {
    const container = textRefs.current[index];
    const selected = window.getSelection();
    setSelectedIndex(index);
    if (
      !container ||
      !selected ||
      selected.isCollapsed ||
      !selected.rangeCount
    ) {
      setSelection(null);
      return;
    }
    const range = selected.getRangeAt(0);
    if (
      !container.contains(range.startContainer) ||
      !container.contains(range.endContainer)
    ) {
      setSelection(null);
      return;
    }
    const prefix = range.cloneRange();
    prefix.selectNodeContents(container);
    prefix.setEnd(range.startContainer, range.startOffset);
    const start = prefix.toString().length;
    const end = start + range.toString().length;
    if (start < end) {
      setSelectedDirection(null);
      setExpandedTakesIndex(null);
      setSelection({ index, start, end });
    }
  };

  const applyDirection = async (paragraph: Paragraph, preset: string) => {
    if (!selection || selection.index !== paragraph.index) return;
    if (
      paragraph.directions.some(
        item => selection.start < item.end && selection.end > item.start
      )
    ) {
      setError('Choose text that does not overlap another direction.');
      return;
    }
    const value = custom.trim();
    if (
      preset === 'custom' &&
      (!value || value.length > 80 || /[\[\]\r\n<>]/.test(value))
    ) {
      setError('Enter a short delivery direction without brackets.');
      return;
    }
    setBusy(`save-${paragraph.index}`);
    setError(null);
    try {
      const directions = [
        ...paragraph.directions,
        {
          start: selection.start,
          end: selection.end,
          preset,
          ...(preset === 'custom' ? { custom: value } : {}),
        },
      ].sort((a, b) => a.start - b.start);
      await request(
        `${chapterBase}/paragraphs/${paragraph.index}/directions`,
        'PUT',
        { directions }
      );
      window.getSelection()?.removeAllRanges();
      setSelection(null);
      setCustom('');
      await refresh();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  const removeDirection = async (paragraph: Paragraph, removeIndex: number) => {
    setBusy(`save-${paragraph.index}`);
    setError(null);
    try {
      await request(
        `${chapterBase}/paragraphs/${paragraph.index}/directions`,
        'PUT',
        {
          directions: paragraph.directions.filter(
            (_, index) => index !== removeIndex
          ),
        }
      );
      setSelectedDirection(null);
      await refresh();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  const generate = async () => {
    if (!pending) return;
    const targets = pending.paragraphs;
    setPending(null);
    setBusy('generate');
    setError(null);
    let started = 0;
    try {
      for (const paragraph of targets) {
        // Each request rechecks its quote and reserves only that paragraph's credits.
        // eslint-disable-next-line no-await-in-loop
        await request(
          `${chapterBase}/paragraphs/${paragraph.index}/generate`,
          'POST',
          {
            confirmCredits: true,
            quotedCredits: paragraph.credits,
            directionHash: paragraph.directionHash,
          }
        );
        started += 1;
      }
      await refresh();
    } catch (err: any) {
      setError(
        started
          ? `${started} of ${targets.length} paragraphs started. ${err.message}`
          : err.message
      );
      await refresh().catch(() => undefined);
    } finally {
      setBusy(null);
    }
  };

  const playTake = async (paragraph: Paragraph, take: Take) => {
    setError(null);
    try {
      const result = await request<{ url: string }>(
        `${chapterBase}/paragraphs/${paragraph.index}/takes/${take.id}/stream-url`
      );
      setPlayingTake(take.id);
      setPlayUrl(result.url);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const useTake = async (paragraph: Paragraph, take: Take) => {
    setBusy(`select-${paragraph.index}`);
    setError(null);
    try {
      await request(
        `${chapterBase}/paragraphs/${paragraph.index}/takes/${take.id}`,
        'PUT'
      );
      setPlayUrl(null);
      setPlayingTake(null);
      setExpandedTakesIndex(null);
      await refresh();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  const packageBook = async () => {
    setBusy('package');
    setError(null);
    try {
      await request(`${base}/package`, 'POST');
      await refresh();
    } catch (err: any) {
      setError('Could not package the audiobook. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const downloadPackage = async (type: 'zip' | 'm4b') => {
    setError(null);
    try {
      const result = await request<{ url: string }>(
        `${base}/package/${type}/stream-url`
      );
      window.open(result.url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const currentCount =
    data?.paragraphs.filter(item => item.audioCurrent).length || 0;
  const targets = data?.paragraphs.filter(item => !item.audioCurrent) || [];
  const chapterTargets = targets.length ? targets : data?.paragraphs || [];
  const chapterCredits = chapterTargets.reduce(
    (sum, paragraph) => sum + paragraph.credits,
    0
  );
  const pendingCredits =
    pending?.paragraphs.reduce(
      (sum, paragraph) => sum + paragraph.credits,
      0
    ) || 0;
  const pendingKeepsCurrent = Boolean(
    pending && data && pending.paragraphs.length < data.paragraphs.length
  );
  const generationRunning = Boolean(data?.paragraphs.some(inProgress));
  const hasGeneratedAudio = Boolean(
    data?.paragraphs.some(item => item.takes.length)
  );

  return (
    <Card data-testid="audiobook-paragraph-studio">
      <Button variant="secondary" size="small" onClick={onClose}>
        <Icon name="arrow left" /> Chapters
      </Button>
      <h3 style={{ margin: '20px 0 4px' }}>
        {data?.title || 'Chapter production'}
      </h3>
      <Muted>
        Select text to shape its delivery; choose a voice cue to manage it. A
        dark line marks current audio; a pale line marks text that needs
        generating.
      </Muted>
      {error && (
        <Alert variant="error">
          {error}
        </Alert>
      )}
      {!data && !error && <p>Loading chapter…</p>}
      {data && (
        <>
          <Toolbar style={{ marginTop: 20 }}>
            <Button
              aria-busy={busy === 'generate'}
              disabled={
                !!busy ||
                generationRunning ||
                chapterTargets.length === 0 ||
                data.balance < chapterCredits
              }
              onClick={() => setPending({ paragraphs: chapterTargets })}
            >
              {hasGeneratedAudio ? 'Regenerate chapter' : 'Generate chapter'} ·{' '}
              {creditLabel(chapterCredits)}
            </Button>
            <Muted>
              {currentCount} of {data.paragraphs.length} paragraphs ready ·{' '}
              {data.balance.toLocaleString()} credits available · Eleven v4
            </Muted>
          </Toolbar>
          {!data.supportsEmotionTags && (
            <Alert variant="warning">
              This narrator model does not support emotion directions. Plain
              narration is available.
            </Alert>
          )}
          {data.chapterStatus === 'ready' &&
            data.chapterModel !== 'eleven_v4' &&
            currentCount === 0 && (
              <Alert variant="info">
                This chapter has audio from an earlier model. Generate it with
                Eleven v4 before packaging.
              </Alert>
            )}
          {data.paragraphs.some(paragraph => paragraph.directions.length) && (
            <Toolbar
              role="group"
              aria-label="Narration direction display"
              style={{ margin: '12px 0 0', gap: 4 }}
            >
              <Muted style={{ marginRight: 4 }}>Narration markers</Muted>
              {(
                [
                  ['color-cue', 'Color + voice cue'],
                  ['tags', '[tags]'],
                ] as const
              ).map(([view, label]) => (
                <Button
                  key={view}
                  variant={markerView === view ? 'primary' : 'secondary'}
                  size="small"
                  aria-pressed={markerView === view}
                  onClick={() => setMarkerView(view)}
                >
                  {label}
                </Button>
              ))}
            </Toolbar>
          )}
          <Manuscript aria-label="Chapter text" data-marker-view={markerView}>
            {data.paragraphs.map(paragraph => {
              const active = selectedIndex === paragraph.index;
              const selectedDirectionIndex =
                selectedDirection?.paragraphIndex === paragraph.index
                  ? paragraph.directions.findIndex(
                      direction =>
                        direction.start === selectedDirection.start &&
                        direction.end === selectedDirection.end
                    )
                  : -1;
              const focusedDirection =
                paragraph.directions[selectedDirectionIndex];
              const focusedPreset = presets.find(
                item => item.id === focusedDirection?.preset
              );
              const focusedLabel = focusedDirection
                ? focusedDirection.custom ||
                  focusedPreset?.label ||
                  focusedDirection.preset
                : '';
              const focusedQuote = focusedDirection
                ? paragraph.text.slice(
                    focusedDirection.start,
                    focusedDirection.end
                  )
                : '';
              const selectedTake = paragraph.takes.find(
                take => take.id === paragraph.selectedTakeId
              );
              const earlierTakes = paragraph.takes
                .map((take, index) => ({ take, index }))
                .filter(({ take }) => take.id !== paragraph.selectedTakeId);
              const historyLabel = `${selectedTake ? 'Earlier takes' : 'Takes'} (${earlierTakes.length})`;
              const marker = inProgress(paragraph)
                ? '#7657af'
                : paragraph.audioCurrent
                  ? '#575757'
                  : paragraph.needsRegeneration
                    ? '#b78a52'
                    : '#d8d8d8';
              const status = inProgress(paragraph)
                ? 'Generating audio'
                : paragraph.audioCurrent
                  ? 'Audio current'
                  : paragraph.needsRegeneration
                    ? 'Audio needs updating'
                    : 'Audio not generated';
              return (
                <React.Fragment key={paragraph.index}>
                  <Passage
                    ref={element => {
                      textRefs.current[paragraph.index] = element;
                    }}
                    data-testid={`audio-paragraph-${paragraph.index}`}
                    data-audio-status={
                      paragraph.audioCurrent ? 'ready' : 'unconverted'
                    }
                    aria-label={`Paragraph ${paragraph.index + 1} text — ${status}`}
                    tabIndex={0}
                    style={{
                      borderLeft: `2px solid ${marker}`,
                      background: active ? '#fbfaf8' : 'transparent',
                    }}
                    onClick={() => {
                      setSelectedIndex(paragraph.index);
                      setSelectedDirection(null);
                      setExpandedTakesIndex(null);
                    }}
                    onMouseUp={() => selectText(paragraph.index)}
                    onKeyUp={() => selectText(paragraph.index)}
                    onTouchEnd={() => selectText(paragraph.index)}
                  >
                    {renderText(paragraph, selectedDirection, direction => {
                      setSelectedIndex(paragraph.index);
                      setSelectedDirection({
                        paragraphIndex: paragraph.index,
                        start: direction.start,
                        end: direction.end,
                      });
                      setSelection(null);
                      setExpandedTakesIndex(null);
                    })}
                  </Passage>
                  {active && (
                    <Context>
                      <Toolbar>
                        <Muted>
                          Paragraph {paragraph.index + 1} · {status}
                        </Muted>
                      </Toolbar>
                      {paragraph.sourceChanged && (
                        <div>
                          The text changed. Generate this paragraph to update
                          its audio.
                        </div>
                      )}
                      {focusedDirection && (
                        <DirectionDetail
                          role="group"
                          aria-label="Selected voice cue"
                        >
                          <span className="direction-label">
                            <Icon name="headphones" />
                            {focusedPreset?.emoji || '✨'} {focusedLabel}
                          </span>
                          <span
                            className="direction-quote"
                            title={focusedQuote}
                          >
                            “{focusedQuote}”
                          </span>
                          <Button
                            variant="ghost"
                            size="small"
                            aria-label={`Remove ${focusedLabel} direction from “${focusedQuote}”`}
                            disabled={!!busy || inProgress(paragraph)}
                            onClick={() =>
                              removeDirection(paragraph, selectedDirectionIndex)
                            }
                          >
                            <Icon name="close" />
                          </Button>
                        </DirectionDetail>
                      )}
                      {data.supportsEmotionTags &&
                        selection?.index === paragraph.index && (
                          <DeliveryPicker>
                            <Muted>
                              <Icon name="headphones" /> Narration delivery for
                              selected text
                            </Muted>
                            <Toolbar>
                              {presets.map(item => (
                                <Button
                                  key={item.id}
                                  variant="secondary"
                                  size="small"
                                  disabled={!!busy || inProgress(paragraph)}
                                  onClick={() =>
                                    applyDirection(paragraph, item.id)
                                  }
                                >
                                  {item.emoji} {item.label}
                                </Button>
                              ))}
                            </Toolbar>
                            <Toolbar>
                              <input
                                aria-label="Custom delivery direction"
                                value={custom}
                                maxLength={80}
                                onChange={event =>
                                  setCustom(event.target.value)
                                }
                                placeholder="Custom delivery"
                              />
                              <Button
                                variant="secondary"
                                size="small"
                                disabled={!!busy || !custom.trim()}
                                onClick={() =>
                                  applyDirection(paragraph, 'custom')
                                }
                              >
                                ✨ Apply
                              </Button>
                            </Toolbar>
                          </DeliveryPicker>
                        )}
                      {paragraph.takes.length > 0 && (
                        <TakesPanel>
                          <Toolbar>
                            {selectedTake && (
                              <Button
                                variant="secondary"
                                size="small"
                                onClick={() =>
                                  playTake(paragraph, selectedTake)
                                }
                              >
                                <Icon name="play" />
                                {paragraph.audioCurrent
                                  ? 'Play current audio'
                                  : 'Play previous audio'}
                              </Button>
                            )}
                            {earlierTakes.length > 0 && (
                              <Button
                                variant="secondary"
                                size="small"
                                aria-expanded={
                                  expandedTakesIndex === paragraph.index
                                }
                                onClick={() =>
                                  setExpandedTakesIndex(
                                    expandedTakesIndex === paragraph.index
                                      ? null
                                      : paragraph.index
                                  )
                                }
                              >
                                {historyLabel}
                                <Icon name="chevron down" />
                              </Button>
                            )}
                          </Toolbar>
                          {expandedTakesIndex === paragraph.index &&
                            earlierTakes.length > 0 && (
                              <div className="take-history">
                                {earlierTakes.map(({ take, index }) => (
                                  <div
                                    className="take-history-row"
                                    key={take.id}
                                  >
                                    <span>
                                      Take {index + 1} ·{' '}
                                      {!take.textCurrent
                                        ? 'Earlier text'
                                        : take.renderCurrent === false
                                          ? 'Earlier rendering'
                                          : 'Available'}
                                    </span>
                                    <Button
                                      variant="secondary"
                                      size="small"
                                      aria-label={`Play take ${index + 1}`}
                                      onClick={() => playTake(paragraph, take)}
                                    >
                                      <Icon name="play" />
                                    </Button>
                                    {take.textCurrent &&
                                      take.renderCurrent !== false && (
                                        <Button
                                          variant="secondary"
                                          size="small"
                                          disabled={
                                            !!busy || inProgress(paragraph)
                                          }
                                          onClick={() =>
                                            useTake(paragraph, take)
                                          }
                                        >
                                          Use take {index + 1}
                                        </Button>
                                      )}
                                  </div>
                                ))}
                              </div>
                            )}
                          {playUrl &&
                            paragraph.takes.some(
                              take => take.id === playingTake
                            ) && (
                              // eslint-disable-next-line jsx-a11y/media-has-caption
                              <audio controls autoPlay src={playUrl} />
                            )}
                        </TakesPanel>
                      )}
                      {paragraph.error && (
                        <Alert variant="error">
                          Could not generate this paragraph. Please try again.
                        </Alert>
                      )}
                    </Context>
                  )}
                </React.Fragment>
              );
            })}
          </Manuscript>
          <Card>
            <strong>Package audiobook</strong>
            <p style={{ margin: '5px 0 12px', color: '#666' }}>
              {data.canPackage
                ? 'All audio is ready. Compile chapter MP3s, ZIP, and M4B without additional narration credits.'
                : data.missingParagraphs
                  ? `${data.missingParagraphs} paragraph(s) still need audio before packaging.`
                  : 'Add manuscript text before packaging.'}
            </p>
            {data.packagingError && (
              <Alert variant="error">
                {data.packagingError}
              </Alert>
            )}
            <Button
              variant="secondary"
              size="small"
              disabled={
                !data.canPackage ||
                !!busy ||
                ['assembling', 'packaging'].includes(data.packagingStatus)
              }
              aria-busy={busy === 'package'}
              onClick={packageBook}
            >
              {data.packagingStatus === 'ready'
                ? 'Rebuild package'
                : 'Package audiobook'}
            </Button>
            {['assembling', 'packaging'].includes(data.packagingStatus) && (
              <Muted> Packaging…</Muted>
            )}
            {data.packagingStatus === 'ready' && (
              <Toolbar>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => downloadPackage('zip')}
                >
                  Download chapter ZIP
                </Button>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => downloadPackage('m4b')}
                >
                  Download M4B
                </Button>
              </Toolbar>
            )}
          </Card>
        </>
      )}
      <Dialog
        open={!!pending}
        title={
          hasGeneratedAudio
            ? 'Regenerate chapter audio?'
            : 'Generate chapter audio?'
        }
        onClose={() => setPending(null)}
        actions={
          <>
            <Button variant="secondary" onClick={() => setPending(null)}>Cancel</Button>
            <Button onClick={generate}>Confirm and generate</Button>
          </>
        }
      >
        <p>{`Produce audio for ${pending?.paragraphs.length || 0} ${pending?.paragraphs.length === 1 ? 'paragraph' : 'paragraphs'}. Cost: ${creditLabel(pendingCredits)}.${pendingKeepsCurrent ? ' Other current paragraphs are kept.' : ''}`}</p>
      </Dialog>
    </Card>
  );
};

export default AudiobookParagraphStudio;
